import { PDPHeroBlock } from "@client/components/PDPHeroBlock";
import { toolboxComponents } from "@client/components/PageSkeleton/Toolbox";
import { ProductCompareBlock } from "@client/components/ProductCompareBlock";
import { ProductDetailsBlock } from "@client/components/ProductDetails";
import { ProductGallery } from "@client/components/ProductGallery";
import { RequestQuoteBlock } from "@client/components/RequestQuote";
import { ResourcesDownloadsBlock } from "@client/components/ResourcesDownloadsBlock";
import { SpecificationsBlock } from "@client/components/SpecificationsBlock";
import React, { useContext } from "react";

import { Context, reverse, templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import {
    firstBlockOverlapsNav,
    isOverlapBlockType,
} from "../components/HeaderNav/overlap";
import { ImageBackgroundCarouselBlock } from "../components/ImageBackground";
import { RelatedProductSlides } from "../components/ImageTextSlides/RelatedProducts";
import { PageSkeleton } from "../components/PageSkeleton";
import {
    ScrollNavMenuBlock,
    ScrollNavMenuItemBlock,
} from "../components/ScrollNavMenuBlock";
import { Actions } from "../components/ScrollNavMenuBlock/Actions";

export const Template = (props: templates.ProductPageTemplate) => {
    const context = useContext(Context);
    const url = reverse("cms-api:download-documents");

    return (
        <PageSkeleton
            {...props.page}
            overlapNav={firstBlockOverlapsNav(
                props.page.body.value.slice(0, 1).find(isOverlapBlockType),
            )}
        >
            <StreamField
                tagName={React.Fragment}
                value={props.page.body.value}
                components={{
                    ...toolboxComponents,
                    pdp_hero: ({ value }) => (
                        <PDPHeroBlock
                            value={value}
                            sku={props.page.product.sku}
                            title={props.page.title}
                            status={props.page.product_status}
                        />
                    ),
                    image_background_carousel: ({ value }) => (
                        <ImageBackgroundCarouselBlock
                            value={value}
                            extraContent={
                                <Actions
                                    hideAboveTablet={true}
                                    product={{
                                        name: props.page.title,
                                        sku: props.page.product.sku,
                                        url: context.request.url,
                                    }}
                                />
                            }
                        />
                    ),
                    scroll_nav_menu: (args) => (
                        <ScrollNavMenuBlock
                            sku={props.page.product.sku}
                            title={props.page.title}
                            {...args}
                        />
                    ),
                    scroll_nav_menu_item: ScrollNavMenuItemBlock,
                    product_details: ({ value }) => (
                        <ProductDetailsBlock
                            umrp={props.page.msrp}
                            documents={props.page.documents}
                            downloadsUrl={url}
                            sku={props.page.product.sku}
                            title={props.page.title}
                            {...value}
                        />
                    ),
                    resources_downloads: ({ value }) => (
                        <ResourcesDownloadsBlock
                            value={value}
                            documents={props.page.documents}
                            downloadsUrl={url}
                        />
                    ),
                    product_gallery: ({ value }) => (
                        <ProductGallery
                            value={value}
                            featured_facets={props.page.featured_facets}
                            msrp={props.page.msrp}
                            status={props.page.product_status}
                        />
                    ),
                    related_products: () =>
                        props.page.related_products.length > 0 ? (
                            <RelatedProductSlides
                                products={props.page.related_products}
                            />
                        ) : (
                            <></>
                        ),
                    product_compare: ({ value, index }) => (
                        <ProductCompareBlock
                            value={value}
                            products={props.page.compare_products}
                            index={index}
                        />
                    ),
                    request_quote: (value) => (
                        <RequestQuoteBlock
                            value={value.value}
                            title={props.page.title}
                            product_image={props.page.downloadlib_tile_image}
                        />
                    ),
                    specifications: SpecificationsBlock,
                }}
            />
        </PageSkeleton>
    );
};
